import {
  Activity,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { liveQuery } from "dexie";
import {
  BookOpen,
  House,
  BarChart3,
  Settings2,
  Plus,
  ArrowRight,
  ChevronRight,
  WifiOff,
  RotateCcw,
} from "lucide-react";
import { initialize, snapshot } from "./db";
import { durationLabel, type Snapshot, type WordSense } from "./domain";
import { analytics } from "./analytics";
import { buildQueue } from "./study";
import { applyTheme, installOffline } from "./platform";
import Editor from "./Editor";
import Library, { WordDetail, type LibraryFilter } from "./Library";
import Study from "./StudyPage";
const Insights = lazy(() => import("./Insights"));
const Settings = lazy(() => import("./Settings"));

const navigation = [
  { id: "today", label: "Bugün", icon: House },
  { id: "library", label: "Kelimeler", icon: BookOpen },
  { id: "insights", label: "Analiz", icon: BarChart3 },
];
const titles: Record<string, string> = {
  today: "Bugün",
  study: "Kelime çalış",
  reviews: "Tekrarlar",
  library: "Kelimelerim",
  insights: "Gelişimin",
  settings: "Ayarlar",
  word: "Kelime ayrıntısı",
};
function current() {
  return location.hash.slice(1) || "today";
}
function routeId(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return "";
  }
}
export default function App() {
  const [s, setS] = useState<Snapshot | null>(null),
    [fatal, setFatal] = useState(""),
    [route, setRoute] = useState(current),
    [edit, setEdit] = useState<WordSense | "new" | null>(null),
    [toast, setToast] = useState(""),
    [online, setOnline] = useState(navigator.onLine),
    [offline, setOffline] = useState("Çevrimdışı kullanım hazırlanıyor…"),
    [update, setUpdate] = useState<ServiceWorkerRegistration | null>(null),
    [filter, setFilter] = useState<LibraryFilter>("all"),
    [libraryKey, setLibraryKey] = useState(0),
    [studyLaunch, setStudyLaunch] = useState(false),
    [studyKind, setStudyKind] = useState<"daily" | "due" | "difficult">("daily"),
    [clock, setClock] = useState(Date.now());
  const main = useRef<HTMLElement>(null),
    started = useRef(false),
    page = route.split("/")[0],
    known = titles[page] ? page : "today";
  useEffect(() => {
    let cancelled = false;
    let sub: { unsubscribe: () => void } | undefined;
    initialize()
      .then(() => {
        if (!cancelled)
          sub = liveQuery(() => snapshot()).subscribe({
            next: (value) => setS(value),
            error: (e) =>
              setFatal(
                "Yerel veriler açılamadı. Tarayıcıda özel gezinmeyi kapatıp yeniden dene. " +
                  String(e),
              ),
          });
      })
      .catch((e) => setFatal(String(e)));
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, []);
  useEffect(() => {
    const handler = () => setRoute(current());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);
  useEffect(() => {
    if (!s) return;
    applyTheme(s.settings);
    const media = matchMedia("(prefers-color-scheme: dark)"),
      f = () => applyTheme(s.settings);
    media.addEventListener("change", f);
    return () => media.removeEventListener("change", f);
  }, [s?.settings]);
  useEffect(() => {
    const f = () => setOnline(navigator.onLine);
    window.addEventListener("online", f);
    window.addEventListener("offline", f);
    const t = setInterval(() => setClock(Date.now()), 15000);
    return () => {
      window.removeEventListener("online", f);
      window.removeEventListener("offline", f);
      clearInterval(t);
    };
  }, []);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void installOffline(setOffline, setUpdate);
    if ("speechSynthesis" in window) speechSynthesis.getVoices();
  }, []);
  useEffect(() => {
    document.title = (titles[known] ?? "Sentence") + " — Sentence";
    if (s) {
      requestAnimationFrame(() => {
        const h =
          main.current?.querySelector<HTMLElement>('[data-page-active="true"] h1') ??
          main.current?.querySelector<HTMLElement>("h1");
        h?.focus({ preventScroll: true });
        const surface = main.current?.querySelector<HTMLElement>(
          '[data-page-active="true"]',
        );
        if (
          surface &&
          document.documentElement.dataset.motion !== "off" &&
          !matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
          surface.animate(
            [
              { opacity: 0, transform: "translateY(8px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            { duration: 240, easing: "cubic-bezier(0.23, 1, 0.32, 1)" },
          );
        }
      });
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [route, !!s]);
  const notify = useCallback((m: string) => setToast(m), []);
  function go(id: string) {
    if (location.hash === "#" + id) setRoute(id);
    else location.hash = id;
  }
  function openWord(w: WordSense) {
    go("word/" + encodeURIComponent(w.id));
  }
  function openLibrary(f: LibraryFilter = "all") {
    setFilter(f);
    setLibraryKey((k) => k + 1);
    go("library");
  }
  function start(kind: "daily" | "due" | "difficult" = "daily") {
    setStudyLaunch(true);
    setStudyKind(kind);
    go(kind === "due" ? "reviews" : "study");
  }
  if (fatal)
    return (
      <main className="fatal">
        <h1>Yerel veriler açılamadı</h1>
        <p>{fatal}</p>
        <p>
          Site verilerini silmeden yeniden yükle. Kaydettiğin bir JSON yedeği varsa
          uygulama açıldıktan sonra Ayarlar’dan geri yükleyebilirsin.
        </p>
        <button onClick={() => location.reload()}>Yeniden dene</button>
      </main>
    );
  if (!s)
    return (
      <main className="loading" aria-busy="true">
        <span className="wordmark">
          Sentence<span>.</span>
        </span>
        <p role="status">Kelime defterin açılıyor…</p>
        <div className="skeleton" />
        <div className="skeleton short" />
      </main>
    );
  const selectedWord =
    page === "word"
      ? s.words.find((w) => w.id === routeId(route.slice(5)) && !w.deleted)
      : undefined;
  return (
    <>
      <a
        href="#main-content"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          main.current?.focus();
        }}
      >
        İçeriğe geç
      </a>
      <div className="app-shell">
        <aside className="sidebar">
          <a className="wordmark" href="#today" aria-label="Sentence ana sayfa">
            Sentence<span>.</span>
          </a>
          <p className="sidebar-subtitle">Cümlelerle İngilizce.</p>
          <nav aria-label="Ana menü">
            {navigation.map((n) => (
              <a
                key={n.id}
                href={"#" + n.id}
                aria-current={
                  known === n.id ||
                  (known === "word" && n.id === "library") ||
                  (known === "study" && n.id === "today")
                    ? "page"
                    : undefined
                }
              >
                <n.icon size={21} />
                {n.label}
              </a>
            ))}
            <a href="#reviews" aria-current={known === "reviews" ? "page" : undefined}>
              <RotateCcw size={21} />
              Tekrarlar
            </a>
          </nav>
          <div className="sidebar-bottom">
            <div className="local-badge">
              <span className="local-dot" />
              Kişisel ve cihazında
            </div>
            <a
              href="#settings"
              aria-current={known === "settings" ? "page" : undefined}
            >
              <Settings2 size={20} />
              Ayarlar
            </a>
          </div>
        </aside>
        <div className="workspace">
          <header className="app-header">
            <a className="wordmark mobile-wordmark" href="#today">
              Sentence<span>.</span>
            </a>
            <span className="desktop-breadcrumb">
              Kelime defterin <span>/</span> {titles[known]}
            </span>
            <div className="header-actions">
              {!online && (
                <span className="offline-badge">
                  <WifiOff size={16} />
                  Çevrimdışı
                </span>
              )}
              <a className="icon-button" href="#settings" aria-label="Ayarları aç">
                <Settings2 size={21} />
              </a>
            </div>
          </header>
          <main ref={main} id="main-content" tabIndex={-1}>
            {update && (
              <div className="notice update-notice">
                <p>Yeni sürüm hazır. Çalışmanı bitirdikten sonra yenileyebilirsin.</p>
                <button
                  className="secondary"
                  onClick={() => {
                    const w = update.waiting;
                    if (w) {
                      navigator.serviceWorker.addEventListener(
                        "controllerchange",
                        () => location.reload(),
                        { once: true },
                      );
                      w.postMessage("ACTIVATE_UPDATE");
                    }
                  }}
                >
                  Yeni sürümü aç
                </button>
              </div>
            )}
            <div data-page-active={known === "today"} hidden={known !== "today"}>
              <Activity mode={known === "today" ? "visible" : "hidden"}>
                <Home
                  s={s}
                  now={clock}
                  onAdd={() => setEdit("new")}
                  onStudy={start}
                  onInsights={() => go("insights")}
                  onOptions={() => {
                    setStudyLaunch(false);
                    go("study");
                  }}
                />
              </Activity>
            </div>
            <div data-page-active={known === "library"} hidden={known !== "library"}>
              <Activity mode={known === "library" ? "visible" : "hidden"}>
                <Library
                  key={libraryKey}
                  s={s}
                  initialFilter={filter}
                  onAdd={() => setEdit("new")}
                  onEdit={setEdit}
                  onDetail={openWord}
                  notify={notify}
                />
              </Activity>
            </div>
            <div data-page-active={known === "insights"} hidden={known !== "insights"}>
              <Activity mode={known === "insights" ? "visible" : "hidden"}>
                <Suspense fallback={<p role="status">Analiz açılıyor…</p>}>
                  <Insights s={s} onWord={openWord} />
                </Suspense>
              </Activity>
            </div>
            {(known === "study" || known === "reviews") && (
              <div data-page-active="true">
                <Study
                  key={known + studyKind}
                  s={s}
                  initialKind={known === "reviews" ? "due" : studyKind}
                  autoStart={studyLaunch}
                  onEdit={setEdit}
                  notify={notify}
                  onLibrary={() => openLibrary()}
                />
              </div>
            )}
            {known === "settings" && (
              <div data-page-active="true">
                <Suspense fallback={<p role="status">Ayarlar açılıyor…</p>}>
                  <Settings s={s} notify={notify} offline={offline} />
                </Suspense>
              </div>
            )}
            {known === "word" && selectedWord && (
              <div data-page-active="true">
                <WordDetail
                  s={s}
                  word={selectedWord}
                  onBack={() => go("library")}
                  onEdit={() => setEdit(selectedWord)}
                  notify={notify}
                />
              </div>
            )}
            {known === "word" && !selectedWord && (
              <div className="empty" data-page-active="true">
                <h1>Kelime bulunamadı</h1>
                <p>Bu kelime silinmiş olabilir.</p>
                <button className="secondary" onClick={() => go("library")}>
                  Kütüphaneyi aç
                </button>
              </div>
            )}
            <footer className="app-footer">
              <span>{online ? offline : "Çevrimdışı · veriler cihazında"}</span>
              <a href="#settings">Yedekleme ve gizlilik</a>
            </footer>
          </main>
        </div>
        <nav
          className="bottom-nav"
          aria-label="Mobil ana menü"
          style={
            {
              "--tab":
                known === "library" || known === "word"
                  ? 1
                  : known === "insights"
                    ? 2
                    : 0,
            } as React.CSSProperties
          }
        >
          {navigation.map((n) => (
            <a
              href={"#" + n.id}
              key={n.id}
              aria-current={
                known === n.id ||
                (["reviews", "study"].includes(known) && n.id === "today") ||
                (known === "word" && n.id === "library")
                  ? "page"
                  : undefined
              }
            >
              <n.icon size={22} />
              <span>{n.label}</span>
            </a>
          ))}
        </nav>
      </div>
      <div className={"toast " + (toast ? "shown" : "")}>
        <div role="status">{toast}</div>
        {toast && (
          <button aria-label="Bildirimi kapat" onClick={() => setToast("")}>
            Kapat
          </button>
        )}
      </div>
      {edit && (
        <Editor
          key={edit === "new" ? "new" : edit.id}
          s={s}
          word={edit === "new" ? undefined : edit}
          onClose={() => setEdit(null)}
          onSaved={notify}
        />
      )}
    </>
  );
}

function Home({
  s,
  now,
  onAdd,
  onStudy,
  onInsights,
  onOptions,
}: {
  s: Snapshot;
  now: number;
  onAdd: () => void;
  onStudy: (kind?: "daily" | "due" | "difficult") => void;
  onInsights: () => void;
  onOptions: () => void;
}) {
  const a = useMemo(() => analytics(s, 30, new Date(now)), [s, now]);
  const hasWords = s.words.some((w) => w.active && !w.deleted);
  const mode =
    s.settings.mode === "collocation" && !s.settings.collocationTests
      ? "understand"
      : s.settings.mode;
  const queue = buildQueue(s, mode, "daily", new Date(now));
  const goal = s.settings.dailyNew;
  const backupDue =
    hasWords &&
    (!s.settings.lastBackup || now - Date.parse(s.settings.lastBackup) > 7 * 86400000);
  return (
    <div className="home simple-home">
      <div className="home-greeting">
        <span className="today-date">
          {new Intl.DateTimeFormat("tr-TR", {
            weekday: "long",
            day: "numeric",
            month: "long",
          }).format(now)}
        </span>
        <h1 tabIndex={-1}>Bugün</h1>
      </div>
      <section className="home-focus" aria-label="Günlük çalışma">
        <div className="home-count">
          <strong>{a.newToday}</strong>
          <span>/ {goal}</span>
        </div>
        <p className="home-count-label">bugün çalışılan yeni kelime</p>
        <progress
          className="daily-progress"
          aria-label="Günlük yeni kelime hedefi"
          max={goal}
          value={Math.min(goal, a.newToday)}
        />
        <h2>
          {hasWords
            ? queue.length
              ? "Bir cümle daha."
              : "Kendi hızında ilerle."
            : "Bir kelimeyle başla."}
        </h2>
        <p className="home-description">
          {hasWords
            ? a.due.length
              ? a.due.length + " tekrar kartın seni bekliyor."
              : queue.length
                ? "Kelimelerin bugün için hazır."
                : "Tekrar zamanı geldiğinde burada göreceksin."
            : "Kelimeyi ekle. Cümlede hatırla."}
        </p>
        <button
          className="primary home-primary"
          onClick={!hasWords ? onAdd : queue.length ? () => onStudy() : onOptions}
        >
          {!hasWords
            ? "İlk kelimemi ekle"
            : queue.length
              ? "Çalışmaya başla"
              : "Çalışma planını aç"}
          {!hasWords ? <Plus size={20} /> : <ArrowRight size={20} />}
        </button>
        {hasWords && (
          <button className="text-button quick-add" onClick={onAdd}>
            <Plus size={18} />
            Kelime ekle
          </button>
        )}
        {!hasWords && (
          <span className="home-footnote">Hazır paket eklemek zorunlu değil.</span>
        )}
      </section>
      <div className="home-summary">
        <button onClick={() => onStudy("due")}>
          <strong>{a.due.length}</strong>
          <span>tekrar bekliyor</span>
          <ChevronRight size={15} />
        </button>
        <button onClick={onInsights}>
          <strong>{durationLabel(a.todayMs)}</strong>
          <span>bugünkü çalışma</span>
          <ChevronRight size={15} />
        </button>
      </div>
      <button className="home-options text-button" onClick={onOptions}>
        Çalışma seçenekleri
        <ChevronRight size={16} />
      </button>
      {backupDue && (
        <a className="backup-inline" href="#settings">
          İlerlemeni koru · Yedek al
          <ChevronRight size={16} />
        </a>
      )}
    </div>
  );
}
